import http from './httpClient';

const BASE = 'notifications';

// Helper to get businessId from localStorage
const getBusinessId = () => {
  return localStorage.getItem('businessId');
};

// Helper to get userId from localStorage
const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.id || user.user_id || user.userId || '1';
  } catch {
    return '1';
  }
};

// Core notification functions
export const getNotifications = async ({ status = 'unread', limit = 50 } = {}) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  
  const params = new URLSearchParams();
  params.append('userId', userId);
  if (status) params.append('status', status);
  if (limit) params.append('limit', limit.toString());
  
  const queryString = params.toString();
  const url = `${BASE}/${businessId}?${queryString}`;
  
  return http.get(`/${url}`);
};

export const getUnreadCount = async () => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.get(`/${BASE}/${businessId}/unread-count?userId=${userId}`);
};

export const markRead = async (ids) => {
  const userId = getUserId();
  return http.post(`/${BASE}/mark-read`, { userId, ids });
};

export const markAllRead = async () => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/mark-all-read`, { userId, businessId });
};

export const dismissNotifications = async (ids) => {
  const userId = getUserId();
  const businessId = getBusinessId();
  return http.post(`/${BASE}/dismiss`, { ids, userId, businessId });
};

export const generateFromStockAlerts = async () => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/generate/stock-alerts`, { businessId, userId });
};

export const syncFromStockAlerts = async () => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/sync/stock-alerts`, { businessId, userId });
};

// Unit Mapping Notification Methods
export const checkUnitMappingSetup = async () => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/unit-mapping/check-setup`, { businessId, userId });
};

export const validateUnit = async (unit, itemName = null) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/unit-mapping/validate-unit`, { businessId, userId, unit, itemName });
};

export const notifyUnitMappingSuccess = async (action, details = null) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/unit-mapping/notify-success`, { businessId, userId, action, details });
};

export const auditUnitMapping = async () => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/unit-mapping/audit`, { businessId, userId });
};

// Ingredient-based mapping notifications
export const checkIncompleteRecipes = async (sampleLimit) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  const body = { businessId, userId };
  if (sampleLimit !== undefined) body.sampleLimit = sampleLimit;
  return http.post(`/${BASE}/ingredient-mapping/check-incomplete-recipes`, body);
};

export const checkUnmappedIngredients = async () => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/ingredient-mapping/check-unmapped-ingredients`, { businessId, userId });
};

export const checkZeroCostRecipes = async () => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/ingredient-mapping/check-zero-cost-recipes`, { businessId, userId });
};

export const alertRecentRecipeChanges = async (days = 7) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/ingredient-mapping/alert-recent-recipe-changes`, { businessId, userId, days });
};

export const checkRecipeStockDiscrepancy = async (servings = 1) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/ingredient-mapping/check-recipe-stock-discrepancy`, { businessId, userId, servings });
};

// Details (read-only) for ingredient-mapping categories
export const getIncompleteRecipeDetails = async (limit = 100) => {
  const businessId = getBusinessId();
  const params = new URLSearchParams();
  params.append('businessId', businessId);
  if (limit) params.append('limit', limit.toString());
  const queryString = params.toString();
  const url = `${BASE}/ingredient-mapping/details/incomplete-recipes?${queryString}`;
  return http.get(`/${url}`);
};

export const getUnmappedIngredientDetails = async (limit = 200) => {
  const businessId = getBusinessId();
  const params = new URLSearchParams();
  params.append('businessId', businessId);
  if (limit) params.append('limit', limit.toString());
  const queryString = params.toString();
  const url = `${BASE}/ingredient-mapping/details/unmapped-ingredients?${queryString}`;
  return http.get(`/${url}`);
};

export const getZeroCostRecipeDetails = async (limit = 200) => {
  const businessId = getBusinessId();
  const params = new URLSearchParams();
  params.append('businessId', businessId);
  if (limit) params.append('limit', limit.toString());
  const queryString = params.toString();
  const url = `${BASE}/ingredient-mapping/details/zero-cost?${queryString}`;
  return http.get(`/${url}`);
};

export const getRecentRecipeChangeDetails = async (days = 7, limit = 200) => {
  const businessId = getBusinessId();
  const params = new URLSearchParams();
  params.append('businessId', businessId);
  if (days) params.append('days', days.toString());
  if (limit) params.append('limit', limit.toString());
  const queryString = params.toString();
  const url = `${BASE}/ingredient-mapping/details/recent-changes?${queryString}`;
  return http.get(`/${url}`);
};

export const getStockDiscrepancyDetails = async (servings = 1, limit = 200) => {
  const businessId = getBusinessId();
  const params = new URLSearchParams();
  params.append('businessId', businessId);
  if (servings) params.append('servings', servings.toString());
  if (limit) params.append('limit', limit.toString());
  const queryString = params.toString();
  const url = `${BASE}/ingredient-mapping/details/stock-discrepancy?${queryString}`;
  return http.get(`/${url}`);
};

// Vendor management notifications
export const vendorIncompleteDetails = async (missingCount, examples = []) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/vendors/incomplete-details`, { businessId, userId, missingCount, examples });
};

export const vendorPerformanceAlert = async (vendorName, metric, currentValue, previousValue) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/vendors/performance-alert`, { businessId, userId, vendorName, metric, currentValue, previousValue });
};

export const newVendorDetected = async (vendorName) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/vendors/new-vendor-detected`, { businessId, userId, vendorName });
};

// OCR & Stock-In notifications
export const ocrSuccessReview = async ({ vendorName = null, date = null, imageId = null } = {}) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/ocr/success-review`, { businessId, userId, vendorName, date, imageId });
};

export const ocrErrorCorrection = async ({ itemName = null, imageId = null, errorMessage = null } = {}) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/ocr/error-correction`, { businessId, userId, itemName, imageId, errorMessage });
};

export const ocrNewVendorDetected = async (vendorName) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/ocr/new-vendor-detected`, { businessId, userId, vendorName });
};

export const ocrUnitNotRecognized = async (unit, itemName = null) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/ocr/unit-not-recognized`, { businessId, userId, unit, itemName });
};

export const ocrDuplicateBillWarning = async ({ vendorName = null, date = null, billNumber = null, amount = null } = {}) => {
  const businessId = getBusinessId();
  const userId = getUserId();
  return http.post(`/${BASE}/ocr/duplicate-bill-warning`, { businessId, userId, vendorName, date, billNumber, amount });
};

export default {
  getNotifications,
  getUnreadCount,
  markRead,
  markAllRead,
  dismissNotifications,
  generateFromStockAlerts,
  syncFromStockAlerts,
  // Unit mapping methods
  checkUnitMappingSetup,
  validateUnit,
  notifyUnitMappingSuccess,
  auditUnitMapping,
  // Vendor methods
  vendorIncompleteDetails,
  vendorPerformanceAlert,
  newVendorDetected,
  // OCR & Stock-In exports
  ocrSuccessReview,
  ocrErrorCorrection,
  ocrNewVendorDetected,
  ocrUnitNotRecognized,
  ocrDuplicateBillWarning,
  // Ingredient-mapping methods
  checkIncompleteRecipes,
  checkUnmappedIngredients,
  checkZeroCostRecipes,
  alertRecentRecipeChanges,
  checkRecipeStockDiscrepancy,
  // Details exports
  getIncompleteRecipeDetails,
  getUnmappedIngredientDetails,
  getZeroCostRecipeDetails,
  getRecentRecipeChangeDetails,
  getStockDiscrepancyDetails
};
